'use client'
import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer'
import { TranslationFunction } from '@/src/types/translations'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { Box, Button, Divider, FormControlLabel, IconButton, Radio, RadioGroup, Stack, Typography } from '@mui/material'
import React from 'react'

interface TravelerType {
    type: 'adults' | 'childs' | 'infants'
    label: string
    subLabel: string
    value: number
    canIncrease: boolean
    canDecrease: boolean
}

interface FlightClass {
    value: string
    label: string
}

interface TravelersSelectorProps {
    open: boolean
    onOpen: () => void
    onClose: () => void
    travelerTypes: TravelerType[]
    flightClasses: FlightClass[]
    flightClass: string
    onFlightClassChange: (newClass: string) => void
    onIncrement: (type: 'adults' | 'childs' | 'infants') => void
    onDecrement: (type: 'adults' | 'childs' | 'infants') => void
    onApply: () => void
    t: TranslationFunction<'common'>
}

const TravelersSelector: React.FC<TravelersSelectorProps> = ({
    open,
    onOpen,
    onClose,
    travelerTypes,
    flightClasses,
    flightClass,
    onFlightClassChange,
    onIncrement,
    onDecrement,
    onApply,
    t
}) => {
    return (
        <SwipeableDrawerWithBackNavigation
            anchor="bottom"
            open={open}
            onClose={onClose}
            onOpen={onOpen}
            sx={{
                height: 'fit-content',
                minHeight: '640px'
            }}
        >
            <Box sx={{ p: 3 }}>
                {/* Title */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    {t('travellerClassHeading')}
                </Typography>

                {/* Travelers Count */}
                <Stack spacing={3}>
                    {travelerTypes.map(({ type, label, subLabel, value, canIncrease, canDecrease }) => (
                        <Box key={type}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="body1" fontWeight={500}>
                                        {label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {subLabel}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <IconButton
                                        onClick={() => onDecrement(type)}
                                        disabled={!canDecrease}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: !canDecrease ? 'grey.300' : 'grey.400',
                                            borderRadius: '50%',
                                            width: 36,
                                            height: 36
                                        }}
                                    >
                                        <RemoveIcon fontSize="small" />
                                    </IconButton>
                                    <Typography variant="body1" sx={{ minWidth: 20, textAlign: 'center' }}>
                                        {value}
                                    </Typography>
                                    <IconButton
                                        onClick={() => onIncrement(type)}
                                        disabled={!canIncrease}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: !canIncrease ? 'grey.300' : 'primary.main',
                                            borderRadius: '50%',
                                            width: 36,
                                            height: 36,
                                            color: !canIncrease ? 'grey.400' : 'primary.main'
                                        }}
                                    >
                                        <AddIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Stack>

                <Divider sx={{ my: 3 }} />

                {/* Flight Class */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {t('class')}
                </Typography>

                <RadioGroup value={flightClass} onChange={e => onFlightClassChange(e.target.value)}>
                    <Stack spacing={1}>
                        {flightClasses.map(({ value, label }) => (
                            <FormControlLabel
                                key={value}
                                value={value}
                                control={<Radio />}
                                label={label}
                                sx={{
                                    m: 0,
                                    '& .MuiFormControlLabel-label': {
                                        flex: 1,
                                        ml: 1
                                    }
                                }}
                            />
                        ))}
                    </Stack>
                </RadioGroup>

                {/* Apply Button */}
                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={onApply}
                    sx={{
                        mt: 4,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600
                    }}
                >
                    {t('apply')}
                </Button>
            </Box>
        </SwipeableDrawerWithBackNavigation>
    )
}

export { TravelersSelector }
export type { TravelersSelectorProps }
